/**
 * Turns the backend merge response's `repointed_counts` into plain-language
 * lines for the success screen (e.g. "3 packages", "450 loyalty points"). The
 * backend returns a per-table row count map; we surface the items a salon owner
 * cares about — appointments/transactions, banked packages, gift cards, loyalty
 * points, saved cards, and notes — and skip anything with a zero/absent count.
 */

interface CountRule {
  key: string;
  singular: string;
  plural: string;
}

// Ordered most-notable first. `loyalty_points_moved` is handled separately
// because it is a point total, not a row count.
const COUNT_RULES: CountRule[] = [
  {
    key: "transactions",
    singular: "appointment / transaction",
    plural: "appointments / transactions",
  },
  { key: "client_service_packages", singular: "package", plural: "packages" },
  { key: "gift_cards", singular: "gift card", plural: "gift cards" },
  {
    key: "client_payment_methods",
    singular: "saved card",
    plural: "saved cards",
  },
  { key: "client_notes", singular: "note", plural: "notes" },
];

const formatLine = (count: number, singular: string, plural: string): string =>
  `${count.toLocaleString()} ${count === 1 ? singular : plural}`;

/**
 * Returns human-readable "what moved" lines for the merged profile, in display
 * order. Empty when there is nothing notable to report (or counts are absent),
 * so callers can fall back to generic copy.
 */
export function summarizeMergeCounts(
  counts?: Record<string, number> | null,
): string[] {
  if (!counts) return [];

  const lines: string[] = [];
  for (const rule of COUNT_RULES) {
    const count = counts[rule.key];
    if (typeof count === "number" && count > 0) {
      lines.push(formatLine(count, rule.singular, rule.plural));
    }
  }

  const points = counts["loyalty_points_moved"];
  if (typeof points === "number" && points > 0) {
    lines.push(formatLine(points, "loyalty point", "loyalty points"));
  }

  return lines;
}
